import React from 'react';
import Icon from '../../../components/AppIcon';
import { emailNotificationService } from '../../../services/emailNotificationService';

const AchievementMilestone = ({ milestone, userPoints, onUnlock }) => {
  const isLocked = !milestone?.isEarned && userPoints < milestone?.requiredPoints;
  const progressPercentage = milestone?.isEarned 
    ? 100 
    : Math.min((userPoints / milestone?.requiredPoints) * 100, 100);

  const handleUnlock = () => {
    if (!isLocked && !milestone?.isEarned) {
      onUnlock?.(milestone);
      
      // Save achievement and send email notification
      const saveAndNotify = async () => {
        try {
          // Save achievement to database
          const saveResult = await emailNotificationService?.saveAchievement({
            achievement_id: milestone?.id || `milestone-${Date.now()}`,
            achievement_title: milestone?.title,
            achievement_description: milestone?.description,
            category: 'milestone',
            points: milestone?.requiredPoints || 0,
            icon: milestone?.icon
          });

          if (saveResult?.success) {
            // Send email notification
            await emailNotificationService?.sendAchievementNotification(
              saveResult?.data?.user_id,
              {
                achievement_title: milestone?.title,
                achievement_description: milestone?.description,
                points: milestone?.requiredPoints || 0
              }
            );
          }
        } catch (error) {
          console.error('Error saving achievement or sending notification:', error);
        }
      };

      saveAndNotify();
    }
  };

  return (
    <div
      className={`relative bg-card rounded-xl p-5 border-2 transition-all ${
        milestone?.isEarned
          ? 'border-amber-500 shadow-lg'
          : isLocked
          ? 'border-dashed border-muted-foreground/30 opacity-60'
          : 'border-primary shadow-md'
      }`}
    >
      {/* Achievement Icon */}
      <div className="flex flex-col items-center mb-4">
        <div
          className={`relative w-20 h-20 rounded-full flex items-center justify-center mb-3 ${
            milestone?.isEarned
              ? 'bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg'
              : isLocked
              ? 'bg-muted border-2 border-dashed border-border' :'bg-gradient-to-br from-blue-400 to-blue-600 shadow-md animate-pulse'
          }`}
        >
          <Icon
            name={milestone?.isEarned ? milestone?.icon : isLocked ? 'Lock' : milestone?.icon}
            size={32}
            className="text-white"
          />
          {milestone?.isEarned && (
            <div className="absolute -top-1 -right-1 w-7 h-7 bg-success rounded-full flex items-center justify-center border-2 border-card">
              <Icon name="Check" size={14} className="text-white" />
            </div>
          )}
        </div>

        {/* Circular Progress for Unlocking */}
        {!milestone?.isEarned && !isLocked && (
          <div className="relative w-16 h-16 -mt-10 mb-2">
            <svg className="transform -rotate-90 w-full h-full absolute inset-0">
              <circle
                cx="50%"
                cy="50%"
                r="45%"
                stroke="var(--color-muted)"
                strokeWidth="3"
                fill="none"
              />
              <circle
                cx="50%"
                cy="50%"
                r="45%"
                stroke="var(--color-primary)"
                strokeWidth="3"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 45} ${2 * Math.PI * 45}`}
                strokeDashoffset={2 * Math.PI * 45 * (1 - progressPercentage / 100)}
                strokeLinecap="round"
                className="transition-all duration-500"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Achievement Name */}
      <h3 className="text-base font-bold text-foreground text-center mb-2 line-clamp-2">
        {milestone?.name}
      </h3>

      {/* Description */}
      <p className="text-xs text-muted-foreground text-center mb-3 line-clamp-2">
        {milestone?.description}
      </p>

      {/* Status */}
      {milestone?.isEarned ? (
        <div className="text-center">
          <div className="inline-flex items-center gap-1 bg-success/10 text-success px-3 py-1 rounded-full text-xs font-medium mb-2">
            <Icon name="CheckCircle" size={12} />
            <span>Obtenu</span>
          </div>
          <p className="text-xs text-muted-foreground">{milestone?.earnedDate}</p>
        </div>
      ) : isLocked ? (
        <div className="text-center">
          <div className="inline-flex items-center gap-1 bg-muted text-muted-foreground px-3 py-1 rounded-full text-xs font-medium mb-2">
            <Icon name="Lock" size={12} />
            <span>Verrouillé</span>
          </div>
          <p className="text-xs text-muted-foreground">{milestone?.requiredPoints} points requis</p>
        </div>
      ) : (
        <div className="text-center">
          <div className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium mb-2">
            <Icon name="Target" size={12} />
            <span>En Cours</span>
          </div>
          <p className="text-xs text-primary font-medium">{Math.round(progressPercentage)}% complété</p>
        </div>
      )}

      {/* Rewards */}
      {milestone?.rewards && milestone?.rewards?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-1 mb-2">
            <Icon name="Gift" size={12} className="text-amber-600" />
            <span className="text-xs font-semibold text-foreground">Récompenses</span>
          </div>
          <div className="space-y-1">
            {milestone?.rewards?.map((reward, index) => (
              <div key={index} className="flex items-center gap-1">
                <Icon name="Star" size={10} className="text-amber-500" />
                <span className="text-xs text-muted-foreground">{reward}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementMilestone;